/**
 * GenerationContext - Cross-tab persistent generation state.
 *
 * Owns the lifecycle of every async backend job (video/audio/image/dubbing/...)
 * so that state survives tab switches and page reloads. Tab hooks call
 * `startJob` after obtaining a backend job id, then read progress + results
 * from the context instead of local `useState`. Polling is owned here via a
 * ref-held Map keyed by jobId, so switching tabs never unmounts the poller.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { STORAGE_KEYS, STORAGE_LIMITS } from '../constants/storage';
import { POLLING_INTERVALS, FAILURE_THRESHOLDS } from '../constants/timings';
// IMPORTANT: import from the barrel, NOT directly from pollAdapterRegistry.
// The barrel (services/polling/index.ts) side-effect-imports every adapter,
// guaranteeing the registry is fully populated before this context resumes
// persisted jobs on startup — regardless of which route/tab is active.
import { getPollAdapter } from '../services/polling';
import { sanitizeBackendError } from '../utils/errorSanitizer';
import type {
  GenerationJob,
  GenerationTabMode,
  JobError,
  PersistedActiveJobs,
  PersistedResults,
  PollUpdate,
  StartJobInput,
} from '../types/generationJob';
import { GenerationCtx, type GenerationContextValue } from './GenerationContext.context';

// -----------------------------------------------------------------------------
// Persistence helpers
// -----------------------------------------------------------------------------

function readJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (err) {
    console.warn(`[GenerationContext] Failed to read ${key}:`, err);
    return null;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn(`[GenerationContext] Failed to write ${key}:`, err);
  }
}

// A job is a "ghost" when the persisted id still exists in localStorage but the
// backend has no record of it — typically after the app was killed mid-run and
// the backend restarted with an empty in-memory registry. Detecting this via
// the first poll response avoids surfacing a bogus "Backend unreachable" banner
// for a job the user can't do anything about.
function isJobNotFoundError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as {
    response?: { status?: number; data?: { error?: { code?: string } } };
  };
  if (e.response?.status === 404) return true;
  if (e.response?.data?.error?.code === 'JOB_NOT_FOUND') return true;
  return false;
}

function getOrCreateOwnerTabId(): { id: string; isFreshSession: boolean } {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEYS.GENERATION_OWNER_TAB_ID);
    if (existing) return { id: existing, isFreshSession: false };
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(STORAGE_KEYS.GENERATION_OWNER_TAB_ID, id);
    return { id, isFreshSession: true };
  } catch {
    return { id: `tab-${Date.now()}`, isFreshSession: true };
  }
}

// -----------------------------------------------------------------------------
// Reducer
// -----------------------------------------------------------------------------

interface State {
  active: Record<string, GenerationJob>;
  results: Partial<Record<GenerationTabMode, GenerationJob[]>>;
}

type Action =
  | { type: 'HYDRATE'; active: Record<string, GenerationJob>; results: State['results'] }
  | { type: 'START'; job: GenerationJob }
  | { type: 'UPDATE'; id: string; patch: Partial<GenerationJob> }
  | { type: 'COMPLETE'; id: string; result: unknown; timing?: GenerationJob['timing'] }
  | { type: 'FAIL'; id: string; error: JobError; status?: 'failed' | 'expired' | 'cancelled' }
  | { type: 'REFRESH_RESULT'; id: string; result: unknown }
  | { type: 'CLEAR_JOB'; id: string }
  | { type: 'CLEAR_RESULTS'; tab: GenerationTabMode };

const initialState: State = { active: {}, results: {} };

function moveToResults(state: State, job: GenerationJob): State {
  const tabResults = state.results[job.tabMode] ?? [];
  const withoutDup = tabResults.filter((r) => r.id !== job.id);
  const next = [job, ...withoutDup].slice(0, STORAGE_LIMITS.MAX_RESULTS_PER_TAB);
  const nextActive = { ...state.active };
  delete nextActive[job.id];
  return {
    active: nextActive,
    results: { ...state.results, [job.tabMode]: next },
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'HYDRATE':
      return { active: action.active, results: action.results };

    case 'START': {
      const active = { ...state.active, [action.job.id]: action.job };
      const ids = Object.keys(active);
      if (ids.length > STORAGE_LIMITS.MAX_ACTIVE_JOBS) {
        const oldest = ids.map((id) => active[id]).sort((a, b) => a.createdAt - b.createdAt)[0];
        if (oldest) delete active[oldest.id];
      }
      return { ...state, active };
    }

    case 'UPDATE': {
      const existing = state.active[action.id];
      if (!existing) return state;
      return {
        ...state,
        active: {
          ...state.active,
          [action.id]: { ...existing, ...action.patch, updatedAt: Date.now() },
        },
      };
    }

    case 'COMPLETE': {
      const existing = state.active[action.id];
      if (!existing) return state;
      const completed: GenerationJob = {
        ...existing,
        status: 'completed',
        progress: 100,
        result: action.result,
        timing: action.timing ?? existing.timing,
        completedAt: Date.now(),
        updatedAt: Date.now(),
      };
      return moveToResults(state, completed);
    }

    case 'FAIL': {
      const existing = state.active[action.id];
      if (!existing) return state;
      const failed: GenerationJob = {
        ...existing,
        status: action.status ?? 'failed',
        error: action.error,
        completedAt: Date.now(),
        updatedAt: Date.now(),
      };
      return moveToResults(state, failed);
    }

    case 'REFRESH_RESULT': {
      const nextResults = { ...state.results };
      for (const tab of Object.keys(nextResults) as GenerationTabMode[]) {
        nextResults[tab] = nextResults[tab]?.map((r) =>
          r.id === action.id ? { ...r, result: action.result, updatedAt: Date.now() } : r
        );
      }
      return { ...state, results: nextResults };
    }

    case 'CLEAR_JOB': {
      const nextActive = { ...state.active };
      delete nextActive[action.id];
      const nextResults = { ...state.results };
      for (const tab of Object.keys(nextResults) as GenerationTabMode[]) {
        nextResults[tab] = nextResults[tab]?.filter((r) => r.id !== action.id);
      }
      return { active: nextActive, results: nextResults };
    }

    case 'CLEAR_RESULTS':
      return { ...state, results: { ...state.results, [action.tab]: [] } };

    default:
      return state;
  }
}

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

export function GenerationProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const hasHydratedRef = useRef(false);
  const pollersRef = useRef<Map<string, { stop: () => void }>>(new Map());
  const ownerTabInfoRef = useRef<{ id: string; isFreshSession: boolean }>(getOrCreateOwnerTabId());
  const ownerTabIdRef = useRef<string>(ownerTabInfoRef.current.id);

  // latest state snapshot for async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // --- persistence (debounced) ---
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      writeJSON(STORAGE_KEYS.GENERATION_ACTIVE_JOBS, state.active);
      writeJSON(STORAGE_KEYS.GENERATION_RECENT_RESULTS, state.results);
    }, 200);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [state]);

  // --- poller manager ---
  const stopPoller = useCallback((jobId: string) => {
    const h = pollersRef.current.get(jobId);
    if (h) {
      h.stop();
      pollersRef.current.delete(jobId);
    }
  }, []);

  const startPoller = useCallback((job: GenerationJob) => {
    if (pollersRef.current.has(job.id)) return;
    const adapter = getPollAdapter(job.tabMode);
    if (!adapter) {
      console.warn(`[GenerationContext] No poll adapter for tab "${job.tabMode}" (job ${job.id}).`);
      return;
    }
    const intervalMs = POLLING_INTERVALS.FAST;
    // No wall-clock maxAttempts: the poller runs indefinitely as long as the
    // backend responds 2xx with a non-terminal status. Failure is triggered
    // exclusively by:
    //   1. N consecutive network/HTTP errors (backend unreachable), OR
    //   2. A terminal status returned by the backend (failed / error).
    // This ensures long-running jobs (e.g. 73-language subtitle translation,
    // 60+ min Track A pipeline) are never killed by a fixed wall-clock timer.
    const MAX_CONSECUTIVE_FAILURES = FAILURE_THRESHOLDS.MAX_CONSECUTIVE_FAILURES;
    let consecutiveFailures = 0;
    let hasEverSucceeded = false;
    let stopped = false;

    const timer = setInterval(async () => {
      if (stopped) return;

      try {
        const update: PollUpdate = await adapter(job.id);
        hasEverSucceeded = true;
        consecutiveFailures = 0;
        if (update.done) {
          stop();
          if (update.status === 'completed') {
            dispatch({
              type: 'COMPLETE',
              id: job.id,
              result: update.result,
              timing: update.timing,
            });
          } else {
            const fallbackError: JobError = update.error ?? {
              message: 'Job failed',
              sanitized: sanitizeBackendError(null),
            };
            dispatch({
              type: 'FAIL',
              id: job.id,
              error: fallbackError,
              status:
                update.status === 'expired' || update.status === 'cancelled'
                  ? update.status
                  : 'failed',
            });
          }
          return;
        }
        dispatch({
          type: 'UPDATE',
          id: job.id,
          patch: {
            status: update.status,
            progress: update.progress ?? stateRef.current.active[job.id]?.progress ?? 0,
            stage: update.stage,
            message: update.message,
            timing: update.timing,
          },
        });
      } catch (err) {
        // Ghost-job cleanup: if the very first poll returns 404 / JOB_NOT_FOUND,
        // this job id was persisted in localStorage from a prior session but
        // the backend's in-memory registry was rebuilt (app killed mid-run).
        // Drop silently — there's nothing to resume and an error banner would
        // just be noise on an otherwise healthy fresh boot.
        if (!hasEverSucceeded && isJobNotFoundError(err)) {
          stop();
          dispatch({ type: 'CLEAR_JOB', id: job.id });
          return;
        }
        consecutiveFailures++;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          stop();
          dispatch({
            type: 'FAIL',
            id: job.id,
            error: {
              message:
                'Backend unreachable after multiple retries. The server may be down or the network connection was lost.',
              code: 'NETWORK_ERROR',
              sanitized: sanitizeBackendError({ error_code: 'NETWORK_ERROR' }),
            },
          });
        }
      }
    }, intervalMs);

    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      pollersRef.current.delete(job.id);
    };

    pollersRef.current.set(job.id, { stop });
  }, []);

  // --- rehydrate on mount (once) ---
  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    const persistedVersion = localStorage.getItem(STORAGE_KEYS.GENERATION_PERSISTENCE_VERSION);
    if (persistedVersion !== __APP_VERSION__) {
      localStorage.removeItem(STORAGE_KEYS.GENERATION_ACTIVE_JOBS);
      localStorage.removeItem(STORAGE_KEYS.GENERATION_RECENT_RESULTS);
      localStorage.setItem(STORAGE_KEYS.GENERATION_PERSISTENCE_VERSION, __APP_VERSION__);
    }
    const activeRaw = readJSON<PersistedActiveJobs>(STORAGE_KEYS.GENERATION_ACTIVE_JOBS) ?? {};
    const resultsRaw = readJSON<PersistedResults>(STORAGE_KEYS.GENERATION_RECENT_RESULTS) ?? {};
    const now = Date.now();
    // Drop ghost active jobs left over from a previous app session / build: if
    // updatedAt is older than the TTL, the owning tab is long dead and the
    // backend job ID is almost certainly invalid. Resuming the poller would
    // only resurrect a fake "Generating..." UI.
    const active: Record<string, GenerationJob> = {};
    for (const [id, job] of Object.entries(activeRaw)) {
      if (now - (job.updatedAt ?? 0) < STORAGE_LIMITS.ACTIVE_JOB_MAX_AGE_MS) {
        active[id] = job;
      }
    }
    const results: State['results'] = {};
    // On a fresh app session (sessionStorage empty -> the renderer was just
    // spawned, e.g. user reopened the desktop app), skip restoring completed
    // results entirely. The previous session's "Video Generated Successfully!"
    // card pointing at a possibly-deleted file on disk is stale UX — the user
    // expects a blank slate on reopen. Active (in-flight) jobs are still
    // resumed below so long-running generations survive the reload.
    if (!ownerTabInfoRef.current.isFreshSession) {
      for (const [tab, jobs] of Object.entries(resultsRaw)) {
        results[tab as GenerationTabMode] = (jobs ?? []).filter((j) => {
          // Drop terminal failures across sessions: a persisted 404 / "Backend
          // unreachable" from a prior build's dead job id is meaningless after
          // restart — the user can't retry it, the backend no longer has it,
          // and surfacing it as a red banner on the fresh app is pure noise.
          if (j.status === 'failed' || j.status === 'expired' || j.status === 'cancelled') {
            return false;
          }
          return !j.completedAt || now - j.completedAt < STORAGE_LIMITS.RESULT_TTL_MS;
        });
      }
    }
    dispatch({ type: 'HYDRATE', active, results });

    // Resume pollers for owned jobs (or adopt stale ownership)
    const ownerId = ownerTabIdRef.current;
    for (const job of Object.values(active)) {
      if (job.status !== 'running' && job.status !== 'queued') continue;
      const stale =
        !job.ownerTabId ||
        job.ownerTabId === ownerId ||
        now - job.updatedAt > STORAGE_LIMITS.JOB_OWNER_STALE_MS;
      if (stale) {
        if (job.ownerTabId !== ownerId) {
          dispatch({ type: 'UPDATE', id: job.id, patch: { ownerTabId: ownerId } });
        }
        startPoller(job);
      }
    }
  }, [startPoller]);

  // --- cross-tab storage sync ---
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEYS.GENERATION_ACTIVE_JOBS || !e.newValue) return;
      try {
        const incoming = JSON.parse(e.newValue) as PersistedActiveJobs;
        const current = stateRef.current.active;
        const merged: Record<string, GenerationJob> = { ...current };
        for (const [id, job] of Object.entries(incoming)) {
          if (!merged[id] || (job.updatedAt ?? 0) > (merged[id].updatedAt ?? 0)) {
            merged[id] = job;
          }
        }
        // Only update if there's a real change
        if (JSON.stringify(merged) !== JSON.stringify(current)) {
          dispatch({ type: 'HYDRATE', active: merged, results: stateRef.current.results });
        }
      } catch (err) {
        console.warn('[GenerationContext] storage sync failed:', err);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // --- stop all pollers on unmount ---
  useEffect(() => {
    const pollers = pollersRef.current;
    return () => {
      for (const handle of pollers.values()) handle.stop();
      pollers.clear();
    };
  }, []);

  // --- public API ---
  const startJob = useCallback(
    <TMeta,>(input: StartJobInput<TMeta>): GenerationJob<unknown, TMeta> => {
      const now = Date.now();
      const job: GenerationJob<unknown, TMeta> = {
        id: input.id,
        tabMode: input.tabMode,
        serverType: input.serverType,
        status: 'queued',
        progress: 0,
        createdAt: now,
        updatedAt: now,
        metadata: input.metadata,
        title: input.title,
        ownerTabId: ownerTabIdRef.current,
      };
      dispatch({ type: 'START', job });
      startPoller(job as GenerationJob);
      return job;
    },
    [startPoller]
  );

  const updateJob = useCallback((id: string, patch: Partial<GenerationJob>) => {
    dispatch({ type: 'UPDATE', id, patch });
  }, []);

  const completeJob = useCallback(
    (id: string, result: unknown, timing?: GenerationJob['timing']) => {
      stopPoller(id);
      dispatch({ type: 'COMPLETE', id, result, timing });
    },
    [stopPoller]
  );

  const failJob = useCallback(
    (id: string, error: JobError, status?: 'failed' | 'expired' | 'cancelled') => {
      stopPoller(id);
      dispatch({ type: 'FAIL', id, error, status });
    },
    [stopPoller]
  );

  const cancelJob = useCallback(
    (id: string) => {
      stopPoller(id);
      dispatch({
        type: 'FAIL',
        id,
        error: { message: 'Cancelled by user', code: 'CANCELLED' },
        status: 'cancelled',
      });
    },
    [stopPoller]
  );

  const clearJob = useCallback(
    (id: string) => {
      stopPoller(id);
      dispatch({ type: 'CLEAR_JOB', id });
    },
    [stopPoller]
  );

  // A finished job's media links are presigned and expire (the worker signs
  // them for an hour) while the result stays on screen for a day. The backend
  // re-signs them on every status read, so re-reading the job is how a result
  // gets live links again. Rejects when the job can no longer be read: the
  // caller must say so instead of handing the user the dead link it holds.
  const refreshResult = useCallback(async (id: string): Promise<unknown> => {
    const job = Object.values(stateRef.current.results)
      .flatMap((list) => list ?? [])
      .find((r) => r.id === id);
    if (!job) throw new Error(`No finished job ${id} to refresh`);
    const adapter = getPollAdapter(job.tabMode);
    if (!adapter) throw new Error(`No poll adapter for tab "${job.tabMode}" (job ${id})`);
    const update = await adapter(id);
    if (update.status !== 'completed') {
      throw new Error(
        update.error?.message ?? `Job ${id} can no longer be read (status: ${update.status})`
      );
    }
    dispatch({ type: 'REFRESH_RESULT', id, result: update.result });
    return update.result;
  }, []);

  const clearResults = useCallback((tab: GenerationTabMode) => {
    dispatch({ type: 'CLEAR_RESULTS', tab });
  }, []);

  const getJob = useCallback((id: string) => stateRef.current.active[id] ?? undefined, []);

  const activeByTabMemo = useMemo(() => {
    const byTab = new Map<GenerationTabMode, GenerationJob[]>();
    for (const job of Object.values(state.active)) {
      const list = byTab.get(job.tabMode) ?? [];
      list.push(job);
      byTab.set(job.tabMode, list);
    }
    for (const list of byTab.values()) list.sort((a, b) => b.createdAt - a.createdAt);
    return byTab;
  }, [state.active]);

  const activeCountsByTab = useMemo(() => {
    const counts = new Map<GenerationTabMode, number>();
    for (const [tab, list] of activeByTabMemo.entries()) counts.set(tab, list.length);
    return counts;
  }, [activeByTabMemo]);

  const getActiveByTab = useCallback(
    (tab: GenerationTabMode) => activeByTabMemo.get(tab) ?? [],
    [activeByTabMemo]
  );

  const getLatestActiveByTab = useCallback(
    (tab: GenerationTabMode) => activeByTabMemo.get(tab)?.[0],
    [activeByTabMemo]
  );

  const getResultsByTab = useCallback(
    (tab: GenerationTabMode) => state.results[tab] ?? [],
    [state.results]
  );

  const getLatestResultByTab = useCallback(
    (tab: GenerationTabMode) => state.results[tab]?.[0],
    [state.results]
  );

  const value = useMemo<GenerationContextValue>(
    () => ({
      startJob,
      updateJob,
      completeJob,
      failJob,
      cancelJob,
      clearJob,
      refreshResult,
      clearResults,
      getJob,
      getActiveByTab,
      getLatestActiveByTab,
      getLatestResultByTab,
      getResultsByTab,
      activeCountsByTab,
      ownerTabId: ownerTabIdRef.current,
    }),
    [
      startJob,
      updateJob,
      completeJob,
      failJob,
      cancelJob,
      clearJob,
      refreshResult,
      clearResults,
      getJob,
      getActiveByTab,
      getLatestActiveByTab,
      getLatestResultByTab,
      getResultsByTab,
      activeCountsByTab,
    ]
  );

  return <GenerationCtx.Provider value={value}>{children}</GenerationCtx.Provider>;
}
